import type { MemberContract, ContractCategory, ContractStatus } from '@/data/seedMember'
import type { RawContract, RawContractKind, RawContractStatus } from '@/types/api'
import { planSlugForBundleReference } from '@/data/bundleReference'
import { guaranteeDetailFor } from '@/data/guaranteeDetails'
import { formatEur } from '@/composables/useSubscription'

/**
 * Maps a raw contract (`GET /contracts`) to the `MemberContract` view-model.
 *
 * Defensive boundary: nullable fields get safe fallbacks. Some display values
 * are localized here (payment method/rhythm, amount), so the mapper takes a
 * translator + locale and must be re-run when the locale changes — that's why
 * the connected space maps through the reactive `useContracts` composable.
 */

type Translate = (key: string, named?: Record<string, unknown>) => string

const KIND_TO_CATEGORY: Record<RawContractKind, ContractCategory> = {
  care: 'sante',
  insurance: 'assurance',
  accommodation: 'assurance',
}

const STATUS_MAP: Record<RawContractStatus, ContractStatus> = {
  active: 'active',
  renewed: 'active',
  terminating: 'terminating',
  terminating_backoffice: 'terminating',
  retracting: 'retracting',
  blocked: 'pending',
  terminated: 'terminated',
  completed: 'terminated',
}

/** View-model status. A backoffice-initiated termination (`terminating_backoffice`)
 *  carries either a retraction or a termination timestamp, which decides the
 *  displayed label (retracting vs terminating). */
function mapStatus(raw: RawContract): ContractStatus {
  if (raw.status === 'terminating_backoffice') {
    if (raw.retraction_requested_at) return 'retracting'
    if (raw.terminating_requested_at) return 'terminating'
  }
  return STATUS_MAP[raw.status] ?? 'active'
}

/** Whole months between two ISO timestamps (calendar-based, ≥ 0). */
function monthsBetween(fromIso: string, untilIso: string): number {
  const from = new Date(fromIso)
  const until = new Date(untilIso)
  if (Number.isNaN(from.getTime()) || Number.isNaN(until.getTime())) return 0
  const months =
    (until.getFullYear() - from.getFullYear()) * 12 + (until.getMonth() - from.getMonth())
  return Math.max(0, months)
}

/** Localized, formatted amount with the cadence suffix (e.g. "10,50 €/mois"). */
function formatAmount(raw: RawContract, rhythm: 'monthly' | 'annual', t: Translate, locale: string): string {
  if (raw.total_amount == null || raw.total_amount === '') return '—'
  const cents = Number(raw.total_amount)
  if (!Number.isFinite(cents)) return '—'
  const suffix = rhythm === 'monthly'
    ? t('auth.account.contracts.perMonth')
    : t('auth.account.contracts.perYear')
  return `${formatEur(cents / 100, locale)}${suffix}`
}

export function mapContract(raw: RawContract, t: Translate, locale: string): MemberContract {
  const planSlug = planSlugForBundleReference(raw.bundle_reference)
  const detail = planSlug ? guaranteeDetailFor(planSlug, locale) : undefined
  const rhythm: 'monthly' | 'annual' = raw.payment_rhythm === 'monthly' ? 'monthly' : 'annual'

  return {
    name: raw.name ?? '',
    reference: raw.contract_number,
    category: KIND_TO_CATEGORY[raw.kind] ?? 'assurance',
    status: mapStatus(raw),
    subscribedOn: raw.subscribed_on,
    general: {
      paymentMode: t(`auth.account.contracts.paymentMethods.${raw.payment_method}`),
      paymentRhythm: t(`auth.account.contracts.rhythms.${rhythm}`),
      paymentAmount: formatAmount(raw, rhythm, t, locale),
      startDate: raw.effective_from,
      endDate: raw.canceled_at ?? raw.effective_until,
      durationMonths: monthsBetween(raw.effective_from, raw.effective_until),
      tacitRenewal: raw.tacit_renewal,
    },
    guarantees: detail?.rows ?? [],
    // Resolved from the same formula slug as the rows above, so the connected
    // space and the sales journey can never link to different documents for the
    // same product. Null for an unmapped bundle reference or a product with no
    // published sheet.
    guaranteeSheetUrl: detail?.sheetUrl ?? null,
  }
}
